// services/modelMenu.js — the model menu the chat and Account read.
//
// The server is the only list (chat-2, 2026-10-30). GET /api/plans/usage
// returns `menu`: Auto first, then every model the server knows, each with
// whether this plan may use it (`allowed`), whether this box can run it
// (`available`, with `unavailableReason`), and the words to show (`bestFor`,
// `cost` 1-3, `low`, `note`). The website used to carry its own copy of the
// menu with prices and rungs; the two drifted, and the picker could offer a
// model the server had already dropped. Now the website never offers an id
// the server does not list: before the server answers, or when it cannot be
// reached, the picker offers Auto alone (FALLBACK_MENU). STATIC_MENU is the
// same list as the server's, for the names and words only.
import { auth } from '../components/FirebaseConfig';
import { API_BASE, fetchWithTimeout } from '../apiSession';
import { browserTimeZone } from './meterFormat';

/** The router: the server picks the model for each turn (services/omni-agent on the server). */
export const AUTO_MODEL = 'auto';
export const AUTO_HINT = 'Picks the best model for each task and uses 20% less of your plan.';
export const AUTO_ENTRY = Object.freeze({
  id: AUTO_MODEL, label: 'Auto', isAuto: true, allowed: true, available: true, hint: AUTO_HINT,
});

// Free always runs Auto, whatever it sends (the server coerces it). Choosing
// a model is a Pro feature: Free sees the models with a "Pro" pill.
export const FREE_MODEL = AUTO_MODEL;

// The model the chat starts on, on every plan, unless the person saved
// another one on Account that this plan may use (the server's `chatModel`).
// It was GPT-4o mini from 2026-09-29, the cheap model, so a fresh session
// never ran on Sonnet by accident; Auto now does that job better: it sends
// quick questions to a quick model and writing to a strong one.
export const DEFAULT_MODEL = AUTO_MODEL;

// The models the server knows, in its order (services/omni-agent/models.js
// MODEL_MENU on the server; Node Backend/test-model-preference.mjs reads the
// ids in this block and holds them to the server's). Names and words only:
// whether a model may be used here is the server's to say, so before it
// answers none of these is offered (FALLBACK_MENU).
export const STATIC_MENU = [
  AUTO_ENTRY,
  { id: 'claude-sonnet-5-5', label: 'Claude Sonnet 5.5', provider: 'anthropic', tier: 'smart', cost: 2, bestFor: 'Best for resumes & cover letters' },
  { id: 'claude-opus-5-5', label: 'Claude Opus 5.5', provider: 'anthropic', tier: 'deep', cost: 3, bestFor: 'Deepest thinking for hard questions' },
  { id: 'claude-haiku-5-5', label: 'Claude Haiku 5.5', provider: 'anthropic', tier: 'fast', cost: 1, bestFor: 'Fastest, for quick questions' },
  { id: 'gpt-6.1-sol', label: 'GPT-6.1 Sol', provider: 'openai', tier: 'smart', cost: 2, bestFor: 'A second opinion' },
  { id: 'gpt-6.1-terra', label: 'GPT-6.1 Terra', provider: 'openai', tier: 'mid', cost: 2, bestFor: 'A lighter second opinion' },
  { id: 'gpt-6-luna', label: 'GPT-6 Luna', provider: 'openai', tier: 'fast', cost: 1, bestFor: 'A fast second opinion' },
  { id: 'gemini-3.1-pro-preview', label: 'Gemini 3.1 Pro', provider: 'google', tier: 'smart', cost: 2, bestFor: 'A second opinion from Google' },
  { id: 'gemini-3.8-flash', label: 'Gemini 3.8 Flash', provider: 'google', tier: 'fast', cost: 1, bestFor: 'Fast answers from Google' },
];

/**
 * What the picker offers before the server answers, or when it cannot be
 * reached: Auto alone. The models are kept, unavailable, so the menu's shape
 * is the server's, and the picker hides every unavailable model.
 */
export const FALLBACK_MENU = STATIC_MENU.map(m => (m.isAuto ? m : { ...m, allowed: false, available: false }));

// The names of the models a reply may have been written by, for a reply
// footer read before the menu has loaded (or a reopened conversation whose
// model has since left the menu). Never offered: only the server's menu is.
export const MODEL_LABELS = {
  ...Object.fromEntries(STATIC_MENU.map(m => [m.id, m.label])),
  // The models before chat-2: older replies keep the name of what wrote them.
  'gpt-4o-mini': 'GPT-4o mini',
  'gpt-4o': 'GPT-4o',
  'claude-haiku-4-5-20251001': 'Claude Haiku 4.5',
  'claude-sonnet-5': 'Claude Sonnet 5',
};

// What an id chosen before chat-2 means now. The server maps these the same
// way (its ALIASES) and answers with them as `chatModel`, which is what the
// chat opens on (modelChoice.js openingModel); this copy is for reading a
// choice when the server's word is not to hand. The two cheap-model ids were
// never a pick (the website seeded them), so they mean Auto.
export const LEGACY_MODEL_IDS = {
  'gpt-4o-mini': AUTO_MODEL,
  'claude-3-5-sonnet': AUTO_MODEL,
  'claude-3-5-sonnet-20240620': AUTO_MODEL,
  'claude-sonnet-5': 'claude-sonnet-5-5',
  'claude-sonnet-4-6': 'claude-sonnet-5-5',
  'claude-haiku-4-5-20251001': 'claude-haiku-5-5',
  'claude-haiku-4-5': 'claude-haiku-5-5',
  'claude-opus-4-7': 'claude-opus-5-5',
  'claude-opus-5': 'claude-opus-5-5',
  'gpt-4o': 'gpt-6.1-sol',
  'gemini-1.5-pro': 'gemini-3.1-pro-preview',
  'gemini-2.5-pro': 'gemini-3.1-pro-preview',
  'gemini-2.5-flash': 'gemini-3.8-flash',
};
export const resolveModelId = (id) => LEGACY_MODEL_IDS[id] || id || DEFAULT_MODEL;

/** Whether a menu row is the Auto router. */
export const isAutoRow = (m) => Boolean(m && (m.isAuto || m.id === AUTO_MODEL));

/** The name of a model, from the menu, else the names above, else the id itself. */
export const labelFor = (id, menu = STATIC_MENU) => {
  if (!id) return '';
  return (menu || []).find(m => m.id === id)?.label || MODEL_LABELS[id] || id;
};

/**
 * The menu as the server gives it for this person, plus the usage summary it
 * rides on (which also carries `meter`, `plan` and `chatModel`). Falls back
 * to Auto alone on any failure, so nothing the server did not list is offered.
 */
export async function fetchModelMenu() {
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) return { menu: FALLBACK_MENU, usage: null };
    // The time zone, so the meter's line says "resets 3:10 pm" in local time.
    const tz = browserTimeZone();
    const url = `${API_BASE}/api/plans/usage${tz ? `?tz=${encodeURIComponent(tz)}` : ''}`;
    const res = await fetchWithTimeout(url, { headers: { Authorization: `Bearer ${token}` } }, 12_000);
    const body = await res.json().catch(() => null);
    if (!res.ok || !body?.ok) return { menu: FALLBACK_MENU, usage: null };
    const menu = Array.isArray(body.menu) && body.menu.length ? body.menu : FALLBACK_MENU;
    return { menu, usage: body };
  } catch {
    return { menu: FALLBACK_MENU, usage: null };
  }
}

/**
 * Where "near the end of the month's AI" starts, as a share of it. One number
 * for the chat and Account's Plan tab, which said 85 and 80 about the same
 * allowance (2026-09-29).
 */
export const NEAR_PCT = 85;
